import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useWebsiteSettings } from '../../context/WebsiteSettingsContext';
import { getFooter } from '../../services/api';
import { FooterSection } from '../../types';
import './Footer.css';

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { settings } = useWebsiteSettings();
  const [footerSections, setFooterSections] = useState<FooterSection[]>([]);

  const currentLang = i18n.language;
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    fetchFooter();
  }, []);

  const fetchFooter = async () => {
    try {
      const response = await getFooter();
      setFooterSections(response.data);
    } catch (error) {
      console.error('Failed to fetch footer:', error);
    }
  };

  // Use dynamic values from settings or fallback to defaults
  const siteName = settings?.site_name || 'Eco Gallery';
  const siteDescription = settings?.site_description || (
    currentLang === 'bn'
      ? 'প্রিমিয়াম কোয়ালিটি এবং সেরা দামে আপনার পছন্দের পণ্য কিনুন।'
      : 'Shop your favorite products with premium quality and best prices.'
  );
  const supportPhone = settings?.support_phone || '+880 1741-818807';
  const supportEmail = settings?.support_email || `support@${siteName.toLowerCase().replace(/\s+/g, '')}.com`;

  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-grid">
          {/* Column 1: Brand & Bio */}
          <div className="footer-col brand-col">
            <div className="footer-brand">
              <h2 className="brand-logo">🛒 {siteName}</h2>
              <p className="brand-bio">
                {siteDescription}
              </p>
              <div className="social-links">
                {settings?.facebook_url && (
                  <a href={settings.facebook_url} className="social-icon" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
                  </a>
                )}
                {settings?.instagram_url && (
                  <a href={settings.instagram_url} className="social-icon" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
                  </a>
                )}
                {settings?.twitter_url && (
                  <a href={settings.twitter_url} className="social-icon" aria-label="Twitter" target="_blank" rel="noopener noreferrer">
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path></svg>
                  </a>
                )}
                {settings?.tiktok_url && (
                  <a href={settings.tiktok_url} className="social-icon" aria-label="TikTok" target="_blank" rel="noopener noreferrer">
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"></path></svg>
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div className="footer-col">
            <h3 className="footer-heading">{t('footer.quickLinks', 'Quick Links')}</h3>
            <ul className="footer-links">
              <li><Link to="/products">Shop All</Link></li>
              <li><Link to="/about">About Us</Link></li>
              <li><Link to="/contact">Contact</Link></li>
              <li><Link to="/track-order">Track Order</Link></li>
            </ul>
          </div>

          {/* Column 3: Customer Support */}
          <div className="footer-col">
            <h3 className="footer-heading">{t('footer.support', 'Customer Support')}</h3>
            <ul className="footer-links">
              <li><Link to="/faq">FAQ</Link></li>
              <li><Link to="/shipping">Shipping Policy</Link></li>
              <li><Link to="/returns">Returns & Exchange</Link></li>
              <li><Link to="/terms">Terms & Conditions</Link></li>
              <li><Link to="/privacy">Privacy Policy</Link></li>
            </ul>
          </div>

          {/* Column 4: Newsletter */}
          <div className="footer-col">
            <h3 className="footer-heading">{t('footer.newsletter', 'Newsletter')}</h3>
            <p className="newsletter-text">
              {currentLang === 'bn'
                ? 'নতুন অফার এবং আপডেট পেতে সাবস্ক্রাইব করুন।'
                : 'Subscribe to get updates on new arrivals & special offers.'}
            </p>
            <form className="newsletter-form" onSubmit={(e) => e.preventDefault()}>
              <div className="input-group">
                <input type="email" placeholder="Your email address" required />
                <button type="submit" aria-label="Subscribe">
                  Subscribe
                </button>
              </div>
            </form>
            <div className="contact-info mt-4">
              {supportEmail && (
                <p>
                  <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
                </p>
              )}
              {supportPhone && (
                <p>
                  <a href={`tel:${supportPhone}`}>{supportPhone}</a>
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <p className="copyright">
            {currentLang === 'bn'
              ? `© ${currentYear === 2026 ? '২০২৬' : currentYear} ${siteName}. সর্বস্বত্ব সংরক্ষিত`
              : `© ${currentYear} ${siteName}. All Rights Reserved.`}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
